import React, { useMemo, useState } from 'react';

import { useScroll, useMotionValueEvent } from 'framer-motion';

import WidgetCard from './WidgetCard';
import WidgetHeader from './_components/WidgetHeader';

import WidgetTalents from './_components/WidgetTalents';
import WidgetLinks from './_components/WidgetLinks';
import WidgetQuote from './_components/WidgetQuote';
import WidgetList from './_components/WidgetList';

import Widgets, { type WidgetsType, type Link } from '.';

/**
 * Floating Widgets Props
 */

type FloatingWidgetsProps = {
  widgets: WidgetsType;
  scrollRef: React.RefObject<HTMLDivElement>;
  relatedServices: Link[];
  relatedArticles: Link[];
};

/**
 * Floating Widgets Components
 */

export default function FloatingWidgets({
  widgets,
  scrollRef,
  relatedArticles,
  relatedServices,
}: FloatingWidgetsProps) {
  const [progress, setProgress] = useState(0);
  const hiringEngineersImages = useMemo(
    () => widgets.hiringEngineers.talents.map((talent) => talent.image.url),
    [],
  );

  const { scrollYProgress } = useScroll({ target: scrollRef, offset: ['start end', 'end end'] });
  useMotionValueEvent(scrollYProgress, 'change', (_progress) => setProgress(_progress));

  const relatedServicesWidgets = relatedServices.length ? 1 : 0;
  const relatedArticlesWidgets = relatedArticles.length ? 1 : 0;

  const totalWidgets = 2 + relatedServicesWidgets + relatedArticlesWidgets;
  const checkProgress = (index: number) => {
    const min = (1 / totalWidgets) * index;
    const max = (1 / totalWidgets) * (index + 1);

    if (progress >= 1) return false;

    return progress >= min && progress <= max;
  };

  return (
    <Widgets className="fixed bottom-2 right-2 md:hidden">
      {checkProgress(0) && (
        <WidgetCard className="bg-site-accent-25 border-site-accent-200" floating>
          <WidgetHeader
            title={widgets.hiringEngineers.title}
            images={hiringEngineersImages}
            className="bg-site-accent-25"
          />
          <WidgetTalents {...widgets.hiringEngineers} />
        </WidgetCard>
      )}

      {!!relatedServices.length && checkProgress(1) && (
        <WidgetCard className="border-site-secondary-green-200" floating>
          <WidgetHeader title={widgets.relatedServices.title} className="bg-site-secondary-green-25" />
          <WidgetLinks links={relatedServices} />
        </WidgetCard>
      )}

      {checkProgress(1 + relatedServicesWidgets) && (
        <WidgetCard className="border-site-neutrals-100" floating>
          <WidgetHeader title={widgets.quote.link.label} className="bg-site-primary-25" />
          <WidgetQuote {...widgets.quote} />
        </WidgetCard>
      )}

      {!!relatedArticles.length && checkProgress(2 + relatedServicesWidgets) && (
        <WidgetCard className="border-site-accent-200" floating>
          <WidgetHeader title={widgets.relatedArticles.title} className="bg-site-accent-25" />
          <WidgetList items={relatedArticles} />
        </WidgetCard>
      )}
    </Widgets>
  );
}
